import { Dtos, SuretyAccountId } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import {
	normalizeToError as asError,
	isStaleVersionConflict,
	logError,
} from "../../../utils/error";
import { useRequest } from "../../../utils/request";
import { accountMetadataQueryKey, accountQueryKey } from "../account.keys";

type MetadataRequest = Dtos.Admin.SuretyAccount.Update.Metadata.Request;

export type MetadataInlineLock = {
	updatedAt: Dtos.Admin.Account.Get.Response["updatedAt"];
	metadata: Dtos.Admin.SuretyAccount.Metadata.Get.Response;
};

type SaveCallbacks = {
	onSuccess?: () => void;
	onError?: (error: Error) => void;
};

/**
 * Metadata update for inline per-card editing. Unlike {@link useAdminUpdateMetadata}, errors are
 * surfaced through the caller's `onError` (rendered as a per-card flag) instead of the global error
 * modal — a single modal is the wrong affordance for a tile-level edit.
 *
 * Because the whole metadata blob shares one optimistic-lock `updatedAt`, two cards edited in quick
 * succession will predictably 409 the second write even though they touch independent fields. Rather
 * than surface that as a conflict, {@link updateMetadata} refetches lean identity and metadata and
 * rebuilds the request against that lock once, so both edits land. Only a non-conflict error — or a
 * second conflict after the retry — reaches `onError`.
 */
export const useAdminUpdateMetadataInline = (accountId: SuretyAccountId) => {
	const { patch } = useRequest();
	const queryClient = useQueryClient();

	const { mutateAsync, isPending } = useMutation({
		mutationFn: async (data: MetadataRequest) => {
			await patch(`/v2/admin/surety/accounts/${accountId}/metadata`, data, "none");
		},
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: accountQueryKey(accountId) }),
				queryClient.invalidateQueries({ queryKey: accountMetadataQueryKey(accountId) }),
			]);
		},
		onError: (error) => {
			if (!isStaleVersionConflict(error)) logError(error);
		},
	});

	const updateMetadata = useCallback(
		async (
			buildRequest: (lock: MetadataInlineLock) => MetadataRequest,
			lock: MetadataInlineLock,
			callbacks?: SaveCallbacks,
		) => {
			try {
				await mutateAsync(buildRequest(lock));
				callbacks?.onSuccess?.();
			} catch (error) {
				if (isStaleVersionConflict(error)) {
					await Promise.all([
						queryClient.refetchQueries({ queryKey: accountQueryKey(accountId) }),
						queryClient.refetchQueries({ queryKey: accountMetadataQueryKey(accountId) }),
					]);
					const freshAccount = queryClient.getQueryData<Dtos.Admin.Account.Get.Response>(
						accountQueryKey(accountId),
					);
					const freshMetadata =
						queryClient.getQueryData<Dtos.Admin.SuretyAccount.Metadata.Get.Response>(
							accountMetadataQueryKey(accountId),
						);
					if (freshAccount && freshMetadata) {
						try {
							await mutateAsync(
								buildRequest({
									updatedAt: freshAccount.updatedAt,
									metadata: freshMetadata,
								}),
							);
							callbacks?.onSuccess?.();
							return;
						} catch (retryError) {
							callbacks?.onError?.(asError(retryError));
							return;
						}
					}
				}
				callbacks?.onError?.(asError(error));
			}
		},
		[accountId, mutateAsync, queryClient],
	);

	return {
		updateMetadata,
		updateMetadataIsLoading: isPending,
	};
};
